import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Badge, Banner, ChipGroup, EmptyState, PageHeader, Spinner } from "@magarac/ui";
import { formatCents } from "@magarac/shared/money";
import { invoiceSchema } from "@magarac/shared/schemas/documents";
import { z } from "zod";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { INVOICE_STATUS_LABELS, dayText, statusTone } from "../../../lib/documents";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/invoices/")({
  component: InvoicesPage,
});

type Filter = "all" | "draft" | "sent" | "partially_paid" | "overdue" | "paid" | "void";

/** CRM-19: every invoice, with overdue ones easy to find. Screen key: invoices.list */
function InvoicesPage() {
  const { active } = useTenant();
  const [filter, setFilter] = useState<Filter>("all");
  const q = useQuery({
    queryKey: ["invoices", active?.id, filter],
    queryFn: () =>
      api.get("/invoices", z.object({ invoices: z.array(invoiceSchema) }), {
        query: filter === "all" ? {} : { status: filter },
      }),
    enabled: Boolean(active),
  });
  const owed = (q.data?.invoices ?? [])
    .filter((i) => i.status !== "void" && i.status !== "draft")
    .reduce((s, i) => s + i.balanceCents, 0);
  return (
    <div>
      <PageHeader title="Invoices" help={<HelpIcon screen="invoices.list" />} />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <ChipGroup
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All" },
            { value: "draft", label: "Drafts" },
            { value: "sent", label: "Sent" },
            { value: "partially_paid", label: "Partly paid" },
            { value: "overdue", label: "Overdue" },
            { value: "paid", label: "Paid" },
            { value: "void", label: "Void" },
          ]}
        />
        {owed > 0 ? (
          <span className="text-sm">Still owed on these: {formatCents(owed)}</span>
        ) : null}
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <Banner tone="danger">{messageFor(q.error)}</Banner>
      ) : q.data.invoices.length === 0 ? (
        <EmptyState
          title={filter === "all" ? "No invoices yet" : "No invoices here"}
          body="Create an invoice from a job: open it and choose Create invoice."
        />
      ) : (
        <ul className="divide-y divide-line rounded-card border border-line bg-surface">
          {q.data.invoices.map((x) => (
            <li key={x.id}>
              <Link
                to="/invoices/$id"
                params={{ id: x.id }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 p-4 hover:bg-brand-50"
              >
                <span className="font-medium">#{x.number}</span>
                <span className="min-w-0 flex-1 truncate">{x.customerName}</span>
                <Badge tone={statusTone(x.status)}>{INVOICE_STATUS_LABELS[x.status]}</Badge>
                <span className="tabular-nums">
                  {x.balanceCents > 0 && x.status !== "void"
                    ? `${formatCents(x.balanceCents)} due`
                    : formatCents(x.totalCents)}
                </span>
                <span className="w-full text-sm text-muted sm:w-auto">
                  {x.dueOn && x.status !== "paid" && x.status !== "void"
                    ? `Due ${dayText(x.dueOn)}`
                    : `Job #${x.jobNumber}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
